import React from 'react'
import { observer } from 'mobx-react'
import { observable } from 'mobx'
import {
  i18nTranslate,
  getNamefromFirstname,
  capitalizeFirstLetter,
  isFeatureEnabled,
} from 'src/utils'
import { Modal } from 'react-bootstrap'
import {
  getSubscriptionPaymentPayloadData,
  getSavedCardGPSOption,
} from 'src/utils/paymentContainerHelper'
import {
  storeContainer,
  addressContainer,
  customerContainer,
  tokenExContainer,
  paymetricContainer,
} from 'src/models'
import {
  getAddressFieldsJsonBasedOnCountry,
  getAddressOnlyFieldsForValidation,
  checkIfTwoFormValuesAreEqual,
  getCardDetails,
  dateFormatDeps,
  getValidationErrorMessage,
  getTransformedBillingAddress,
} from 'src/deps'
import { PaymentMarketForm } from 'src/views/components'
import { toastState } from 'src/views/components/Toast'
import { AddressValidation } from 'src/views/components/AddressValidation'
import { PaymentIcon } from 'src/views/components/PaymentIcon'
import { overlayState } from 'src/views/components/Overlay'
import './styles.scss'

@observer
class SubscriptionPaymentEditModal extends React.Component {
  @observable isToShowBillingAddressValidationModal = false
  @observable isBillingAddressInvalid = false
  @observable recommendationList = []
  @observable accountFormData = {}

  /**
   * @description Returns the appropriate payment container based on Paymetric flag
   * @returns {Object} tokenExContainer or paymetricContainer
   */
  getPaymentContainer = () => {
    return this.enableDirectPaymetricAndAccertifyIntegration()
      ? paymetricContainer
      : tokenExContainer
  }

  enableDirectPaymetricAndAccertifyIntegration = () =>
    isFeatureEnabled('enableDirectPaymetricAndAccertifyIntegration')

  getFormFieldsBasedOnCountry = async () => {
    let billingAddressFieldJson = await getAddressFieldsJsonBasedOnCountry(
      storeContainer?.defaultShippingRegion
    )
    let billingAddressFormFields = await getAddressOnlyFieldsForValidation(
      billingAddressFieldJson,
      'subscription'
    )
    return billingAddressFormFields
  }

  handlePaymentModalToggleView = () => {
    this.props.toggleSubscriptionPaymentEditModal()
  }

  handleEditPaymentApi = async (paymentCardDetails = {}) => {
    const { handleEditSubscriptionPayment = function () {} } = this.props
    const editPaymentId = this.props.selectedPaymentData?.id || ''
    const updatePaymentPayload = {
      id: editPaymentId,
      payload: paymentCardDetails,
    }
    await customerContainer.editSubscriptionPayment(updatePaymentPayload, true)
    handleEditSubscriptionPayment(editPaymentId)
  }
  handleBillingAddressSuggestionModalContinue = async (formData = {}) => {
    const {
      cardFirstName: cardHolderName = '',
      cardNumber = '',
      expiryDate = '',
      provider = '',
      setToDefaultPayment = false,
    } = this.accountFormData

    const cardExpirationMonth = expiryDate?.split?.('/')?.[0] || '01'
    const cardExpirationYear = expiryDate?.split?.('/')?.[1] || '20'
    const selectedBillingAddress = {
      ...formData,
    }
    const { properties, provider: normalizedProvider } =
      getSubscriptionPaymentPayloadData({
        paymentContainer: this.getPaymentContainer(),
        paymetricContainer,
        provider,
        gpsPaymentOption: getSavedCardGPSOption(this.props.selectedPaymentData),
        isGPSOptionResolved: true,
      })

    const reqParams = {
      accountInfo: {
        name: cardHolderName,
        lastName: cardHolderName,
        cardNumber,
        expiryMonth: cardExpirationMonth,
        expiryYear: '20' + cardExpirationYear,
        provider: normalizedProvider,
        isDefault: setToDefaultPayment,
      },
      billingAddress: selectedBillingAddress,
      properties,
    }

    if (formData?.selectedIndex <= 0) {
      let validateOriginalAddressResponse =
        await addressContainer.originalAddressApiCall(formData)
      this.handleBillingAddressSuggestionModalClose()
      if (addressContainer.isSuccessResponse(validateOriginalAddressResponse)) {
        this.handlePaymentModalToggleView()
        this.handleEditPaymentApi(reqParams)
      } else {
        const errorMessageToDisplay = getValidationErrorMessage(
          validateOriginalAddressResponse
        )
        toastState.setToastMessage(errorMessageToDisplay)
      }
    } else {
      this.handleBillingAddressSuggestionModalClose()
      this.handlePaymentModalToggleView()
      this.handleEditPaymentApi(reqParams)
    }
  }

  handleBillingAddressSuggestionModalClose = () => {
    this.isToShowBillingAddressValidationModal = false
  }

  handleAddNewPaymentContinueButton = async (cardDetails = {}) => {
    const {
      cardFirstName = '',
      setDefaultSubscriptionPayment = false,
      setToDefaultPayment = false,
      billingSameAsShipping = false,
      paymentMethodType = '',
      cardNumber = '',
      expiryDate = '',
      provider = '',
      ...addressParams
    } = cardDetails

    this.accountFormData = cardDetails
    const { addressData = [], selectedAddressId = 0 } = this.props

    let selectedSubscriptionAddress =
      addressData?.filter(
        address => address?.addressId === selectedAddressId
      ) || []
    const billingAddress =
      billingSameAsShipping === true && selectedSubscriptionAddress?.length > 0
        ? selectedSubscriptionAddress?.[0]
        : addressParams

    billingAddress.email = customerContainer?.profileResponse?.email || ''

    const transformedBillingAddress =
      getTransformedBillingAddress(billingAddress)

    delete addressParams.undefined
    // `type` is no longer sent on payment edit, for card or GPS payment types
    const editPaymentPayload = {
      isDefault: setToDefaultPayment,
      isDefaultSubscriptionPayment: setDefaultSubscriptionPayment,
    }

    const name = getNamefromFirstname(cardFirstName) || ''
    if (
      paymentMethodType === 'CREDITCARD' ||
      paymentMethodType === 'DEBITCARD'
    ) {
      const { properties, provider: normalizedProvider } =
        getSubscriptionPaymentPayloadData({
          paymentContainer: this.getPaymentContainer(),
          paymetricContainer,
          provider,
          gpsPaymentOption: getSavedCardGPSOption(
            this.props.selectedPaymentData
          ),
          isGPSOptionResolved: true,
        })
      editPaymentPayload.accountInfo = {
        name: name?.firstName,
        lastName: name?.lastName,
        cardNumber,
        expiryMonth: expiryDate?.split?.('/')?.[0],
        expiryYear: '20' + expiryDate?.split?.('/')?.[1],
        provider: normalizedProvider,
        isDefault: setToDefaultPayment,
      }
      editPaymentPayload.billingAddress = transformedBillingAddress
      editPaymentPayload.properties = properties
    }

    if (!billingSameAsShipping) {
      const transformedAddressFormData = addressParams || {}
      const billingAddressData = {
        ...transformedAddressFormData,
        default: true,
        street1: transformedAddressFormData?.addressLine1 || '',
        street2: transformedAddressFormData?.addressLine2 || '',
        zipCode:
          transformedAddressFormData?.zip ||
          transformedAddressFormData?.postalCode ||
          '',
      }
      delete billingAddressData.undefined
      this.handleAddNewPaymentContinue(addressParams, billingAddressData)
    } else {
      overlayState.showLoader()
      const selectedPaymentId = this.props.selectedPaymentData?.id || ''
      const updatedPaymentData = {
        id: selectedPaymentId,
        payload: editPaymentPayload,
      }
      const updatePaymentApiResponse =
        await customerContainer.editSubscriptionPayment(
          updatedPaymentData,
          true
        )

      if (customerContainer.isSuccessResponse(updatePaymentApiResponse)) {
        if (typeof window === 'object') {
          window.scrollTo(0, 0)
        }
        overlayState.hideLoader()
        this.handlePaymentModalToggleView()
        this.props.handleEditSubscriptionPayment(selectedPaymentId)
      } else {
        this.handlePaymentModalToggleView()
        overlayState.hideLoader()
      }
    }
  }

  handleAddNewPaymentContinue = async (formData = {}, reqBody = {}) => {
    const editPaymentFormDataTemp = {
      ...formData,
      zip: formData?.zip || formData?.postalCode || '',
      shippingInstructions: reqBody?.shippingInstructions || '',
      setDefaultAddress: reqBody?.setDefaultAddress || false,
      country: reqBody?.country || '',
    }

    const { isToMakeValidationApiCall = false } = formData

    const isToMakeValidationApiForPayment =
      reqBody?.country === 'US' ||
      reqBody?.country === 'CA' ||
      isToMakeValidationApiCall

    let billingAddressFormFields = await this.getFormFieldsBasedOnCountry()

    if (isToMakeValidationApiForPayment) {
      const billingAddressValidationResponse =
        await addressContainer.validateAddress(reqBody)
      const recommendationAddress =
        billingAddressValidationResponse?.address?.validationDetails?.[0]
          ?.recommendations || []
      if (billingAddressValidationResponse?.code === 'ADCOVAL0003') {
        this.recommendationList = [{ ...editPaymentFormDataTemp }, []]
        this.isBillingAddressInvalid = true
        this.isToShowBillingAddressValidationModal = true
      } else if (
        billingAddressValidationResponse?.code == 'ONESOURCE_ADPLGIN_VAL_ERROR'
      ) {
        this.isToShowBillingAddressValidationModal = false
        toastState.setToastMessage(
          i18nTranslate(
            'payment.updateFailure',
            'Unable to update payment, please try again'
          )
        )
        return
      } else if (
        addressContainer.isSuccessResponse(billingAddressValidationResponse)
      ) {
        let firstBillingAddress = recommendationAddress?.[0] || {}
        let isFirstSuggestedAddressSameAsCurrentAddress =
          await checkIfTwoFormValuesAreEqual(
            billingAddressFormFields,
            editPaymentFormDataTemp,
            firstBillingAddress
          )

        if (
          Object.keys(firstBillingAddress)?.length > 0 &&
          !isFirstSuggestedAddressSameAsCurrentAddress
        ) {
          this.recommendationList = [
            { ...editPaymentFormDataTemp },
            ...recommendationAddress,
          ]
          this.isBillingAddressInvalid = false
          this.isToShowBillingAddressValidationModal = true
        } else {
          this.handleBillingAddressSuggestionModalContinue(
            editPaymentFormDataTemp
          )
        }
      } else {
        this.isToShowBillingAddressValidationModal = false
        toastState.setToastMessage(
          i18nTranslate(
            'payment.updateFailure',
            'Unable to update payment, please try again'
          )
        )
        return
      }
    } else {
      this.handleBillingAddressSuggestionModalContinue(editPaymentFormDataTemp)
    }
  }

  renderAddNewPaymentForm = () => {
    const { selectedPaymentData = {}, subscriptionPaymentId = '' } = this.props
    const cardData = getCardDetails(selectedPaymentData?.accountInfo) || {}
    const billingAddress = selectedPaymentData?.billingAddress || {}
    const isDefault = cardData?.isDefault || false
    const isSubscriptionDefaultPayment =
      selectedPaymentData?.id?.toString() === subscriptionPaymentId?.toString()
        ? true
        : false

    const iconType = cardData?.provider?.toLowerCase()
    const currentDateTimeStamp = Date.now()
    const { month, year } =
      dateFormatDeps.convertTimeStampToDate(currentDateTimeStamp)
    let isCardExpired =
      year < cardData?.expiryYear
        ? true
        : year == cardData?.expiryYear
          ? month <= cardData?.expiryMonth
            ? true
            : false
          : false

    const endingText = i18nTranslate('payment.cardEndingTxt', '-****')
    const expiryText = i18nTranslate('payment.cardExpiresText', 'Expires')
    const expiredText = i18nTranslate('payment.expiredText', 'Expired')
    return (
      <>
        <div className="d-flex payment-edit-card-details-wrapper">
          <h5 className="payment-edit-card-wrapper ml-2 mb-0">
            <PaymentIcon type={iconType} />
            <span className="mr-1 ml-1">
              {capitalizeFirstLetter(cardData?.provider)}
            </span>
            <span className="mr-1">
              {endingText}
              {cardData?.cardNumber}
            </span>
          </h5>
          <span
            className={
              isCardExpired
                ? 'paragraph-s ml-1 d-flex card-expiry-text align-items-center'
                : 'paragraph-s text-danger ml-1 d-flex card-expiry-text align-items-center'
            }>
            <span>{isCardExpired ? expiryText : expiredText}&nbsp;</span>
            <span>{`${cardData?.expiryMonth}/${cardData?.expiryYear?.substr(
              2
            )}`}</span>
          </span>
        </div>
        <PaymentMarketForm
          onSubmitForm={this.handleAddNewPaymentContinueButton}
          isPaymentEdit={true}
          isFromEditSubscription={true}
          includePaymetricCvv={false}
          className="payment-edit-form-wrapper"
          isDefault={isDefault}
          billingAddressType="subscription"
          editUpdateModalEnabled={true}
          billingAddress={billingAddress}
          formType="subscription-market-form"
          isSubscriptionDefaultPayment={isSubscriptionDefaultPayment}
        />
      </>
    )
  }

  render() {
    const {
      customModalTitle = '',
      isToShowPaymentEditModal = false,
      paymentData = [],
    } = this.props
    return (
      <>
        <Modal
          aria-hidden={false}
          centered
          data-testid="qa-multiple-subscription-saved-payment-modal"
          show={isToShowPaymentEditModal}
          key="payment-edit-modal"
          className="subscription-saved-payment-edit-modal"
          onHide={this.handlePaymentModalToggleView}>
          <Modal.Header closeButton>
            <Modal.Title
              as="h4"
              className="mb-0 subscription-saved-payment-edit-modal-title"
              data-testid="qa-subscription-saved-payment-modal-title">
              {customModalTitle}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body
            className="subscription-saved-payment-edit-modal-body"
            data-testid="qa-subscription-saved-payment-modal-body">
            {this.renderAddNewPaymentForm()}
          </Modal.Body>
        </Modal>
        {this.isToShowBillingAddressValidationModal && (
          <AddressValidation
            open={this.isToShowBillingAddressValidationModal}
            addressInvalidFlag={this.isBillingAddressInvalid}
            recommendations={this.recommendationList}
            handleClose={this.handleBillingAddressSuggestionModalClose}
            handleClickOnPro={this.handleBillingAddressSuggestionModalContinue}
          />
        )}
      </>
    )
  }
}

export { SubscriptionPaymentEditModal }
export default SubscriptionPaymentEditModal
